import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Components
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import LoginPage from './pages/Auth/LoginPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';

// Pages
import DashboardPage from './pages/Dashboard/DashboardPage';
import CasesPage from './pages/Cases/CasesPage';
import CaseFolderPage from './pages/Cases/CaseFolderPage';
import ClientsPage from './pages/Clients/ClientsPage';
import IntakePipelinePage from './pages/Clients/IntakePipelinePage';
import DocumentsPage from './pages/Documents/DocumentsPage';
import MatterDrivePage from './pages/Drive/MatterDrivePage';
import PublicSharePage from './pages/Drive/PublicSharePage';
import TasksPage from './pages/Tasks/TasksPage';
import CalendarPage from './pages/Calendar/CalendarPage';
import BillingPage from './pages/Billing/BillingPage';
import InvoicesPage from './pages/Billing/InvoicesPage';
import ReportsPage from './pages/Reports/ReportsPage';
import AnalyticsPage from './pages/Analytics/AnalyticsPage';
import SecurityPage from './pages/Security/SecurityPage';
import SettingsPage from './pages/Settings/SettingsPage';
import HelpPage from './pages/Help/HelpPage';
import NotificationsPage from './pages/Notifications/NotificationsPage';
import TenantPage from './pages/Tenant/TenantPage';
import TenantRegistrationPage from './pages/TenantRegistration/TenantRegistrationPage';
import AcceptTenantInvitePage from './pages/Auth/AcceptTenantInvitePage';
import ProfilePage from './pages/Profile/ProfilePage';
import TimeEntriesPage from './pages/TimeEntries/TimeEntriesPage';
import UsersPage from './pages/Users/UsersPage';
import SubscriptionPage from './pages/Subscription/SubscriptionPage';
import CmsPage from './pages/Cms/CmsPage';
import GrowthAnalyticsPage from './pages/Growth/GrowthAnalyticsPage';
import PortalLoginPage from './pages/Portal/PortalLoginPage';
import PortalDashboardPage from './pages/Portal/PortalDashboardPage';
import PortalSignPage from './pages/Portal/PortalSignPage';
import PortalInboxPage from './pages/Portal/PortalInboxPage';
import MailPage from './pages/Mail/MailPage';
import PortalForgotPasswordPage from './pages/Portal/PortalForgotPasswordPage';
import PortalSetPasswordPage from './pages/Portal/PortalSetPasswordPage';
import ExpenseApprovalsPage from './pages/Billing/ExpenseApprovalsPage';
import StaffWorkloadPage from './pages/Reports/StaffWorkloadPage';
import ReportCenterPage from './pages/Reports/ReportCenterPage';
import CourtFormsPage from './pages/CourtForms/CourtFormsPage';
import UserGuidePage from './pages/Help/UserGuidePage';

function App() {
  return (
    <div className="App">
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/register" element={<TenantRegistrationPage />} />
        <Route path="/accept-invite" element={<AcceptTenantInvitePage />} />
        <Route path="/portal/login" element={<PortalLoginPage />} />
        <Route path="/portal/forgot-password" element={<PortalForgotPasswordPage />} />
        <Route path="/portal/set-password" element={<PortalSetPasswordPage />} />
        <Route path="/portal/reset-password" element={<ResetPasswordPage />} />
        <Route path="/portal/sign/:requestId" element={<PortalSignPage />} />
        <Route path="/portal" element={<PortalDashboardPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout>
                <DashboardPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <DashboardPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cases"
          element={
            <ProtectedRoute>
              <Layout>
                <CasesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cases/:caseId/folder"
          element={
            <ProtectedRoute>
              <Layout>
                <CaseFolderPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients"
          element={
            <ProtectedRoute>
              <Layout>
                <ClientsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/intake-pipeline"
          element={
            <ProtectedRoute>
              <Layout>
                <IntakePipelinePage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/templates"
          element={
            <ProtectedRoute>
              <Layout>
                <DocumentsPage key="firm-templates" mode="templates" />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <Layout>
                <DocumentsPage key="matter-documents" mode="matters" />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/drive"
          element={
            <ProtectedRoute requiredPermissions={['documents:read']}>
              <Layout>
                <MatterDrivePage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route path="/share/:token" element={<PublicSharePage />} />
        <Route
          path="/tasks"
          element={
            <ProtectedRoute>
              <Layout>
                <TasksPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar"
          element={
            <ProtectedRoute>
              <Layout>
                <CalendarPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute requiredPermissions={['billing:read']}>
              <Layout>
                <BillingPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoices"
          element={
            <ProtectedRoute requiredPermissions={['billing:read']}>
              <Layout>
                <InvoicesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portal-inbox"
          element={
            <ProtectedRoute requiredPermissions={['clients:read']}>
              <Layout>
                <PortalInboxPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/mail"
          element={
            <ProtectedRoute requiredPermissions={['emails:read']}>
              <Layout>
                <MailPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/expense-approvals"
          element={
            <ProtectedRoute requiredPermissions={['billing:write']}>
              <Layout>
                <ExpenseApprovalsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff-workload"
          element={
            <ProtectedRoute requireAdmin>
              <Layout>
                <StaffWorkloadPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/court-forms"
          element={
            <ProtectedRoute requiredPermissions={['documents:read']}>
              <Layout>
                <CourtFormsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/report-center"
          element={
            <ProtectedRoute requiredPermissions={['reports:read']}>
              <Layout>
                <ReportCenterPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute requiredPermissions={['reports:read']}>
              <Layout>
                <ReportsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute requireAdmin>
              <Layout>
                <AnalyticsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription"
          element={
            <ProtectedRoute requireAdmin>
              <Layout>
                <SubscriptionPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cms"
          element={
            <ProtectedRoute requireAdmin>
              <Layout>
                <CmsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/growth"
          element={
            <ProtectedRoute requirePlatformAdmin>
              <Layout>
                <GrowthAnalyticsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/security"
          element={
            <ProtectedRoute requireAdmin>
              <Layout>
                <SecurityPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute requireAdmin>
              <Layout>
                <SettingsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/help/guide"
          element={
            <ProtectedRoute>
              <Layout>
                <UserGuidePage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <Layout>
                <HelpPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Layout>
                <NotificationsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant"
          element={
            <ProtectedRoute requireAdmin>
              <Layout>
                <TenantPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Layout>
                <ProfilePage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/time-entries"
          element={
            <ProtectedRoute>
              <Layout>
                <TimeEntriesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute requireAdmin>
              <Layout>
                <UsersPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App; 